import React, { useState, useEffect, useMemo } from 'react';
import { 
  MagnifyingGlassIcon, 
 
  ArrowPathIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  Cog6ToothIcon,
  EyeIcon,
  LinkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { unmatchedApi, purchaseOrderApi, inventoryApi } from '../services/api';
import { useUIActions } from '../store';
import { UnmatchedItem, UnmatchedStatus, PurchaseOrder } from '../types';
import LoadingSpinner from './LoadingSpinner';

interface UnmatchedStats {
  total: number;
  waiting: number;
  processing: number;
  resolved: number;
  average_waiting_days: number;
}

interface MatchSuggestion {
  item_id: string;
  product_name: string;
  po_id: string;
  vendor_code: string;
  similarity: number;
  reasons: string[];
}

const UnmatchedPage: React.FC = () => {
  // State management
  const [items, setItems] = useState<UnmatchedItem[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [stats, setStats] = useState<UnmatchedStats>({
    total: 0,
    waiting: 0,
    processing: 0,
    resolved: 0,
    average_waiting_days: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vendorFilter, setVendorFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('waiting_days');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [matchSuggestions, setMatchSuggestions] = useState<Record<string, MatchSuggestion[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { addToast } = useUIActions();

  // Mock data for development
  const mockUnmatchedItems: UnmatchedItem[] = [
    {
      temp_id: 'UNM001',
      serial_number: 'SN20250902001',
      product_name: 'iPhone 15 Pro Max 256GB',
      vendor_code: '005',
      received_date: '2025-09-01T10:00:00Z',
      waiting_days: 2,
      status: UnmatchedStatus.WAITING,
      notes: '序號格式異常，可能為新批次'
    },
    {
      temp_id: 'UNM002',
      imei: '358240051111110',
      product_name: 'Samsung Galaxy S24 Ultra',
      vendor_code: '006',
      received_date: '2025-08-30T15:30:00Z',
      waiting_days: 4,
      status: UnmatchedStatus.WAITING,
      notes: 'IMEI無法匹配現有對帳單'
    },
    {
      temp_id: 'UNM003',
      serial_number: 'MAC20250825999',
      product_name: 'MacBook Pro 14" M3',
      vendor_code: '005',
      received_date: '2025-08-25T09:15:00Z',
      waiting_days: 9,
      status: UnmatchedStatus.ABNORMAL,
      notes: '商品名稱不符，待人工確認'
    },
    {
      temp_id: 'UNM004',
      serial_number: 'WATCH2025001',
      product_name: 'Apple Watch Series 9',
      vendor_code: '007',
      received_date: '2025-08-28T14:20:00Z',
      waiting_days: 6,
      status: UnmatchedStatus.WAITING
    },
    {
      temp_id: 'UNM005',
      imei: '358240051111111',
      product_name: 'iPad Pro 12.9" M2',
      vendor_code: '005',
      received_date: '2025-08-31T11:45:00Z',
      waiting_days: 3,
      status: UnmatchedStatus.WAITING,
      notes: '可能為跨期商品'
    }
  ];

  const mockPurchaseOrders: PurchaseOrder[] = [
    {
      po_id: 'PO-005-20250901',
      vendor_code: '005',
      vendor_name: '蘋果授權經銷商',
      po_date: '2025-09-01',
      total_qty: 150,
      matched_qty: 142,
      status: 'in_progress' as any,
      created_at: '2025-09-01T08:00:00Z',
      updated_at: '2025-09-02T12:00:00Z',
      created_by: 'system'
    },
    {
      po_id: 'PO-006-20250830',
      vendor_code: '006',
      vendor_name: '三星台灣',
      po_date: '2025-08-30',
      total_qty: 89,
      matched_qty: 88,
      status: 'in_progress' as any,
      created_at: '2025-08-30T10:00:00Z',
      updated_at: '2025-09-02T10:30:00Z',
      created_by: 'system'
    }
  ];

  // Initialize data
  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    setLoading(!showRefreshing);

    try {
      // 獲取未匹配項目
      const unmatchedResponse = await unmatchedApi.list({
        limit: 1000 // 獲取更多資料
      });
      if (unmatchedResponse.success && unmatchedResponse.data) {
        const unmatchedItems = Array.isArray(unmatchedResponse.data) ? unmatchedResponse.data : unmatchedResponse.data.items || [];
        
        // 轉換資料格式以匹配前端介面
        const formattedItems = unmatchedItems.map((item: any) => ({
          temp_id: item.id || item.temp_id,
          serial_number: item.serial_number,
          imei: item.imei,
          product_name: item.scanned_product_name || item.product_name || '未知商品',
          vendor_code: item.vendor_code,
          received_date: item.scan_date || item.created_at || new Date().toISOString(),
          waiting_days: item.waiting_days || Math.floor((new Date().getTime() - new Date(item.scan_date || item.created_at).getTime()) / (1000 * 60 * 60 * 24)),
          status: item.match_status === 'unmatched' ? UnmatchedStatus.WAITING : 
                  item.match_status === 'matched' ? UnmatchedStatus.MATCHED :
                  item.match_status === 'abnormal' ? UnmatchedStatus.ABNORMAL : UnmatchedStatus.WAITING,
          notes: item.notes || item.reason
        }));
        
        setItems(formattedItems);
        
        // 計算統計資料
        const newStats: UnmatchedStats = {
          total: formattedItems.length,
          waiting: formattedItems.filter(item => item.status === UnmatchedStatus.WAITING).length,
          processing: 0, // 暫時設為0，等後端實現processing狀態
          resolved: formattedItems.filter(item => item.status === UnmatchedStatus.MATCHED).length,
          average_waiting_days: formattedItems.length > 0 ? 
            formattedItems.reduce((sum: number, item: any) => sum + (item.waiting_days || 0), 0) / formattedItems.length : 0
        };
        setStats(newStats);
      } else {
        // API 失敗的後備處理
        console.warn('未匹配項目 API 未返回有效資料，使用模擬資料');
        setItems(mockUnmatchedItems);
        const newStats: UnmatchedStats = {
          total: mockUnmatchedItems.length,
          waiting: mockUnmatchedItems.filter(item => item.status === UnmatchedStatus.WAITING).length,
          processing: 0,
          resolved: mockUnmatchedItems.filter(item => item.status === UnmatchedStatus.MATCHED).length,
          average_waiting_days: mockUnmatchedItems.reduce((sum, item) => sum + item.waiting_days, 0) / mockUnmatchedItems.length
        };
        setStats(newStats);
      }

      // 獲取採購單列表
      try {
        const poResponse = await purchaseOrderApi.list();
        if (poResponse.success && poResponse.data) {
          const poItems = Array.isArray(poResponse.data) ? poResponse.data : poResponse.data.items || [];
          setPurchaseOrders(poItems);
        } else {
          setPurchaseOrders(mockPurchaseOrders);
        }
      } catch (error) {
        console.error('Failed to load purchase orders:', error);
        setPurchaseOrders(mockPurchaseOrders);
      }

      // Generate mock match suggestions
      const suggestions: Record<string, MatchSuggestion[]> = {};
      mockUnmatchedItems.forEach(item => {
        if (item.status === UnmatchedStatus.WAITING) {
          suggestions[item.temp_id] = [
            {
              item_id: 'EXP001',
              product_name: item.product_name,
              po_id: `PO-${item.vendor_code}-20250901`,
              vendor_code: item.vendor_code,
              similarity: 85,
              reasons: ['商品名稱相似度85%', '廠商代碼匹配']
            },
            {
              item_id: 'EXP002',
              product_name: item.product_name + ' (新版)',
              po_id: `PO-${item.vendor_code}-20250825`,
              vendor_code: item.vendor_code,
              similarity: 72,
              reasons: ['商品名稱相似度72%', '可能為版本差異']
            }
          ];
        }
      });
      setMatchSuggestions(suggestions);

    } catch (error) {
      console.error('載入未匹配項目失敗:', error);
      addToast({
        type: 'error',
        title: '載入失敗',
        message: '無法載入未匹配項目資料'
      });
    } finally {
      setLoading(false);
      if (showRefreshing) setRefreshing(false);
    }
  };

  // Filtered and sorted items
  const filteredItems = useMemo(() => {
    let filtered = items;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.imei?.includes(searchQuery) ||
        item.vendor_code.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    // Vendor filter
    if (vendorFilter !== 'all') {
      filtered = filtered.filter(item => item.vendor_code === vendorFilter);
    }

    // Sort items
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'waiting_days':
          aValue = a.waiting_days;
          bValue = b.waiting_days;
          break;
        case 'received_date':
          aValue = new Date(a.received_date);
          bValue = new Date(b.received_date);
          break;
        case 'product_name':
          aValue = a.product_name;
          bValue = b.product_name;
          break;
        case 'vendor_code':
          aValue = a.vendor_code;
          bValue = b.vendor_code;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [items, searchQuery, statusFilter, vendorFilter, sortBy, sortOrder]);

  // Pagination
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredItems, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  // Unique vendors for filter
  const vendors = useMemo(() => {
    const uniqueVendors = Array.from(new Set(items.map(item => item.vendor_code)));
    return uniqueVendors.map(code => ({
      code,
      name: purchaseOrders.find(po => po.vendor_code === code)?.vendor_name || `廠商${code}`
    }));
  }, [items, purchaseOrders]);

  // Handlers
  const handleRefresh = () => {
    fetchData(true);
  };

  const handleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
    setShowBatchActions(newSelected.size > 0);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === paginatedItems.length) {
      setSelectedItems(new Set());
      setShowBatchActions(false);
    } else {
      setSelectedItems(new Set(paginatedItems.map(item => item.temp_id)));
      setShowBatchActions(true);
    }
  };

  const handleResolveItem = async (itemId: string, resolution: string, notes?: string) => {
    try {
      // 調用真實的 API
      const response = await unmatchedApi.resolve(itemId, resolution, notes);
      
      if (response.success) {
        // 更新本地狀態
        setItems(prev => prev.map(item => 
          item.temp_id === itemId 
            ? { ...item, status: resolution as UnmatchedStatus, notes }
            : item
        ));

        addToast({
          type: 'success',
          title: '處理成功',
          message: `項目 ${itemId} 已標記為 ${getStatusDisplayName(resolution as UnmatchedStatus)}`
        });
        
        // 重新載入資料以確保同步
        setTimeout(() => fetchData(true), 1000);
      } else {
        throw new Error(response.message || '處理失敗');
      }
    } catch (error: any) {
      console.error('處理未匹配項目失敗:', error);
      addToast({
        type: 'error',
        title: '處理失敗',
        message: error.message || '無法處理該項目，請稍後再試'
      });
    }
  };

  const handleManualMatch = async (unmatchedId: string, expectedItemId: string) => {
    try {
      // 調用真實的手動匹配 API
      const result = await inventoryApi.manualMatch(unmatchedId, expectedItemId);
      
      if (result.success) {
        setItems(prev => prev.map(item => 
          item.temp_id === unmatchedId 
            ? { ...item, status: UnmatchedStatus.MATCHED }
            : item
        ));

        addToast({
          type: 'success',
          title: '匹配成功',
          message: result.data?.message || `手動匹配已完成，批號: ${result.data?.batch_id}`
        });
        
        // 重新載入資料
        setTimeout(() => fetchData(true), 1000);
      } else {
        throw new Error(result.message || '手動匹配失敗');
      }
    } catch (error: any) {
      console.error('手動匹配失敗:', error);
      addToast({
        type: 'error',
        title: '匹配失敗',
        message: error.message || '手動匹配失敗，請稍後再試'
      });
    }
  };

  const handleBatchAction = async (action: string) => {
    if (selectedItems.size === 0) return;

    try {
      setLoading(true);
      
      // 批量處理選中的項目
      const promises = Array.from(selectedItems).map(async (itemId) => {
        try {
          const response = await unmatchedApi.resolve(itemId, action);
          return { itemId, success: response.success };
        } catch (error) {
          return { itemId, success: false };
        }
      });
      
      const results = await Promise.all(promises);
      const successCount = results.filter(r => r.success).length;
      const failedCount = results.length - successCount;
      
      if (successCount > 0) {
        addToast({
          type: 'success',
          title: '批量處理完成',
          message: `成功處理 ${successCount} 個項目${failedCount > 0 ? `，${failedCount} 個失敗` : ''}`
        });
      }
      
      if (failedCount > 0 && successCount === 0) {
        addToast({
          type: 'error',
          title: '批量處理失敗',
          message: `${failedCount} 個項目處理失敗`
        });
      }

      setSelectedItems(new Set());
      setShowBatchActions(false);
      fetchData();
      
    } catch (error) {
      console.error('批量處理失敗:', error);
      addToast({
        type: 'error',
        title: '批量操作失敗',
        message: '批量操作失敗，請稍後再試'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoMatch = async (poId: string) => {
    try {
      setLoading(true);
      
      // 調用真實的自動匹配 API
      const response = await unmatchedApi.autoMatch(poId);
      
      if (response.success) {
        addToast({
          type: 'success',
          title: '自動匹配完成',
          message: response.data?.message || `跨期自動匹配已完成，共匹配 ${response.data?.matched || 0} 個項目`
        });

        fetchData();
      } else {
        throw new Error(response.message || '自動匹配失敗');
      }
      
    } catch (error: any) {
      console.error('自動匹配失敗:', error);
      addToast({
        type: 'error',
        title: '自動匹配失敗',
        message: error.message || '自動匹配過程中發生錯誤'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    try {
      setDeleting(true);
      
      const response = await unmatchedApi.delete(itemId);
      
      if (response.success) {
        addToast({
          type: 'success',
          title: '刪除成功',
          message: '未匹配項目已刪除'
        });

        setDeleteConfirm(null);
        fetchData();
      } else {
        throw new Error(response.message || '刪除失敗');
      }
      
    } catch (error: any) {
      console.error('刪除失敗:', error);
      addToast({
        type: 'error',
        title: '刪除失敗',
        message: error.message || '刪除過程中發生錯誤'
      });
    } finally {
      setDeleting(false);
    }
  };

  // Utility functions
  const getStatusDisplayName = (status: UnmatchedStatus): string => {
    switch (status) {
      case UnmatchedStatus.WAITING:
        return '待處理';
      case UnmatchedStatus.MATCHED:
        return '已匹配';
      case UnmatchedStatus.ABNORMAL:
        return '異常';
      case UnmatchedStatus.RETURNED:
        return '已退回';
      default:
        return '未知';
    }
  };

  const getStatusColor = (status: UnmatchedStatus): string => {
    switch (status) {
      case UnmatchedStatus.WAITING:
        return 'bg-yellow-100 text-yellow-800';
      case UnmatchedStatus.MATCHED:
        return 'bg-green-100 text-green-800';
      case UnmatchedStatus.ABNORMAL:
        return 'bg-red-100 text-red-800';
      case UnmatchedStatus.RETURNED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getWaitingDaysColor = (days: number): string => {
    if (days >= 7) return 'text-red-600 font-bold';
    if (days >= 3) return 'text-yellow-600 font-medium';
    return 'text-green-600';
  };

  if (loading && !refreshing) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">未匹配項目</h1>
          <p className="mt-2 text-sm text-gray-600">
            管理未匹配的商品和異常處理
            {stats && (
              <span className="ml-2 text-xs text-gray-400">
                最後更新: {new Date().toLocaleString('zh-TW')}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? '更新中...' : '刷新'}
          </button>
          <button
            onClick={() => handleAutoMatch('PO-005-20250901')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <LinkIcon className="w-4 h-4 mr-2" />
            跨期匹配
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-100">
              <ExclamationTriangleIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">總數量</p>
              <p className="text-xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-yellow-100">
              <ClockIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">待處理</p>
              <p className="text-xl font-bold text-yellow-600">{stats.waiting}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-100">
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">已處理</p>
              <p className="text-xl font-bold text-green-600">{stats.resolved}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-100">
              <ClockIcon className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">平均等待</p>
              <p className="text-xl font-bold text-purple-600">
                {stats.average_waiting_days.toFixed(1)} 天
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-indigo-100">
              <DocumentArrowDownIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">處理率</p>
              <p className="text-xl font-bold text-indigo-600">
                {stats.total > 0 ? ((stats.resolved / stats.total) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜尋商品名稱、序號、IMEI..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full sm:w-80"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">全部狀態</option>
              <option value={UnmatchedStatus.WAITING}>待處理</option>
              <option value={UnmatchedStatus.ABNORMAL}>異常</option>
              <option value={UnmatchedStatus.MATCHED}>已匹配</option>
              <option value={UnmatchedStatus.RETURNED}>已退回</option>
            </select>

            {/* Vendor Filter */}
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">全部廠商</option>
              {vendors.map(vendor => (
                <option key={vendor.code} value={vendor.code}>
                  {vendor.name} ({vendor.code})
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-3">
            {/* Sort Options */}
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="waiting_days-desc">等待天數 (高到低)</option>
              <option value="waiting_days-asc">等待天數 (低到高)</option>
              <option value="received_date-desc">接收日期 (新到舊)</option>
              <option value="received_date-asc">接收日期 (舊到新)</option>
              <option value="product_name-asc">商品名稱 (A-Z)</option>
              <option value="vendor_code-asc">廠商代碼 (A-Z)</option>
            </select>

            <span className="text-sm text-gray-500">
              共 {filteredItems.length} 項
            </span>
          </div>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {showBatchActions && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-primary-600 mr-2" />
              <span className="text-sm text-primary-700">
                已選擇 {selectedItems.size} 個項目
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleBatchAction('resolve')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                批量標記已處理
              </button>
              <button
                onClick={() => handleBatchAction('abnormal')}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                批量標記異常
              </button>
              <button
                onClick={() => {
                  setSelectedItems(new Set());
                  setShowBatchActions(false);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                取消選擇
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Items Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">未匹配項目列表</h3>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={selectedItems.size === paginatedItems.length && paginatedItems.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-500">全選</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  選擇
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  商品資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  識別碼
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  廠商
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  等待天數
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedItems.map((item) => (
                <React.Fragment key={item.temp_id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.temp_id)}
                        onChange={() => handleSelectItem(item.temp_id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                          <Cog6ToothIcon className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                          <p className="text-xs text-gray-500">ID: {item.temp_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {item.serial_number && (
                          <div>
                            <span className="font-medium text-gray-700">序號:</span>
                            <p className="font-mono text-gray-900">{item.serial_number}</p>
                          </div>
                        )}
                        {item.imei && (
                          <div>
                            <span className="font-medium text-gray-700">IMEI:</span>
                            <p className="font-mono text-gray-900">{item.imei}</p>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <p className="font-medium text-gray-900">{item.vendor_code}</p>
                        <p className="text-gray-500">
                          {purchaseOrders.find(po => po.vendor_code === item.vendor_code)?.vendor_name || '未知廠商'}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <ClockIcon className="w-4 h-4 text-gray-400 mr-1" />
                        <span className={`text-sm font-medium ${getWaitingDaysColor(item.waiting_days)}`}>
                          {item.waiting_days} 天
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(item.received_date).toLocaleDateString('zh-TW')}
                      </p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                        {getStatusDisplayName(item.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setExpandedItem(expandedItem === item.temp_id ? null : item.temp_id)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          {expandedItem === item.temp_id ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleResolveItem(item.temp_id, UnmatchedStatus.MATCHED)}
                          className="text-green-600 hover:text-green-900"
                          title="標記已匹配"
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleResolveItem(item.temp_id, UnmatchedStatus.ABNORMAL)}
                          className="text-red-600 hover:text-red-900"
                          title="標記異常"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(item.temp_id)}
                          className="text-gray-600 hover:text-gray-900"
                          title="刪除項目"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Row */}
                  {expandedItem === item.temp_id && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50">
                        <div className="space-y-4">
                          {/* Notes */}
                          {item.notes && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="flex items-start">
                                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 mr-2 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-yellow-800">備註</p>
                                  <p className="text-sm text-yellow-700">{item.notes}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Match Suggestions */}
                          {matchSuggestions[item.temp_id] && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-3">匹配建議</h4>
                              <div className="space-y-2">
                                {matchSuggestions[item.temp_id].map((suggestion, index) => (
                                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">
                                          {suggestion.product_name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          採購單: {suggestion.po_id} | 廠商: {suggestion.vendor_code}
                                        </p>
                                        <div className="flex items-center mt-1">
                                          <div className="flex items-center mr-4">
                                            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                              <div
                                                className="bg-green-600 h-2 rounded-full"
                                                style={{ width: `${suggestion.similarity}%` }}
                                              />
                                            </div>
                                            <span className="text-xs text-gray-600">{suggestion.similarity}%</span>
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {suggestion.reasons.join(', ')}
                                          </div>
                                        </div>
                                      </div>
                                      <button
                                        onClick={() => handleManualMatch(item.temp_id, suggestion.item_id)}
                                        className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                                      >
                                        手動匹配
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {paginatedItems.length === 0 && (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg">沒有找到符合條件的未匹配項目</p>
              {(searchQuery || statusFilter !== 'all' || vendorFilter !== 'all') && (
                <p className="text-gray-400 text-sm mt-2">試著調整搜尋條件或篩選器</p>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                上一頁
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  顯示第 <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, filteredItems.length)}
                  </span>{' '}
                  項，共 <span className="font-medium">{filteredItems.length}</span> 項
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    上一頁
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    下一頁
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
          <EyeIcon className="w-5 h-5 mr-2" />
          使用說明
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
          <div className="space-y-2">
            <p>• 未匹配項目是指掃描後無法在對帳單中找到匹配商品的項目</p>
            <p>• 系統會自動提供匹配建議，您可以進行手動匹配</p>
            <p>• 可以使用「跨期匹配」功能匹配不同採購單的商品</p>
          </div>
          <div className="space-y-2">
            <p>• 等待天數越長的項目優先級越高，建議優先處理</p>
            <p>• 支援批量操作來提高處理效率</p>
            <p>• 異常項目需要人工介入處理</p>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="mt-4 text-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  確認刪除
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500">
                    您確定要刪除這個未匹配項目嗎？此操作無法復原。
                  </p>
                </div>
                <div className="flex justify-center space-x-4 mt-4">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    disabled={deleting}
                    className="px-4 py-2 bg-gray-300 text-gray-800 text-sm font-medium rounded-md hover:bg-gray-400 disabled:opacity-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => handleDeleteItem(deleteConfirm)}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    {deleting ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                        刪除中...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="w-4 h-4 mr-2" />
                        確認刪除
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnmatchedPage;